<script lang="ts" setup>
import { TuiButton } from '@clickbar/tailwindui-vue'

import { processAndUploadImage } from '../page-builder'

import type { PageElementDataOfType } from '../page-builder'

const tenant = useTenant()
const data = defineModel<PageElementDataOfType<'slideshow'>>('data', { required: true })

const uploading = ref(false)
const uploadError = ref('')
const uploadProgress = ref(0)

async function chooseImages() {
  const input = document.createElement('input')
  input.type = 'file'
  input.multiple = true
  input.accept = 'image/*'

  input.addEventListener(
    'change',
    async () => {
      const files = [...(input.files ?? [])]
      if (files.length === 0) return

      uploading.value = true
      uploadProgress.value = 0
      uploadError.value = ''

      const uploadedUrls: string[] = []
      const fileProgresses = new Map<File, number>()

      // Create a function to update total progress
      const updateProgress = () => {
        const totalProgress = [...fileProgresses.values()].reduce((sum, p) => sum + p, 0)
        uploadProgress.value = (totalProgress / files.length) * 100
      }

      // Upload all files concurrently
      await Promise.all(
        files.map(async (file) => {
          fileProgresses.set(file, 0)
          const url = await processAndUploadImage(file, tenant, (progress) => {
            fileProgresses.set(file, progress)
            updateProgress()
          })
          if (url) uploadedUrls.push(url)
        }),
      )

      if (uploadedUrls.length < files.length) {
        uploadError.value = 'Bei einigen Uploads ist ein Fehler aufgetreten.'
      }

      data.value.urls.push(...uploadedUrls)
      uploading.value = false
    },
    { once: true },
  )

  input.click()
}

function removeImage(index: number) {
  data.value.urls.splice(index, 1)
}

function moveImage(index: number, direction: 'left' | 'right') {
  if (
    (direction === 'left' && index === 0) ||
    (direction === 'right' && index === data.value.urls.length - 1)
  ) {
    return
  }

  const urls = [...data.value.urls]
  const targetIndex = direction === 'left' ? index - 1 : index + 1
  ;[urls[index], urls[targetIndex]] = [urls[targetIndex], urls[index]]
  data.value.urls = urls
}
</script>

<template>
  <div class="space-y-4 select-none" @click.stop>
    <div class="flex items-center justify-center">
      <div v-if="!uploading" class="flex flex-col items-center">
        <TuiButton @click="chooseImages">
          <template #icon="iconProps">
            <IMingcuteUpload2Fill v-bind="iconProps" />
          </template>
          {{ data.urls.length === 0 ? 'Bilder auswählen' : 'Weitere Bilder hinzufügen' }}
        </TuiButton>

        <p v-if="uploadError" class="text-xs text-red-500 mt-1">{{ uploadError }}</p>
      </div>

      <div v-else>
        <p class="text-xs text-center font-medium mb-0.5">Hochladen...</p>

        <div class="w-64 rounded-full overflow-hidden">
          <TuiProgressLinear :value="uploadProgress" class="w-full" color="indigo" />
        </div>
      </div>
    </div>

    <div v-if="data.urls.length > 0" class="relative">
      <div class="overflow-x-auto pb-2 -mx-2 px-2">
        <div class="flex gap-2">
          <div
            v-for="(url, index) in data.urls"
            :key="url"
            class="relative shrink-0 w-40 aspect-[3/2] group"
          >
            <img
              :src="url"
              class="size-full object-cover rounded border border-zinc-200"
              draggable="false"
            />

            <!-- Move buttons -->
            <div
              class="absolute top-1 left-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <button
                type="button"
                class="bg-white/80 hover:bg-white rounded-full p-1 shadow-sm transition-opacity disabled:cursor-not-allowed"
                :class="{ 'opacity-30 hover:bg-white/80': index === 0 }"
                :disabled="index === 0"
                @click.stop="moveImage(index, 'left')"
              >
                <IHeroiconsChevronLeft class="size-4 text-zinc-600" />
              </button>

              <button
                type="button"
                class="bg-white/80 hover:bg-white rounded-full p-1 shadow-sm transition-opacity disabled:cursor-not-allowed"
                :class="{ 'opacity-30 hover:bg-white/80': index === data.urls.length - 1 }"
                :disabled="index === data.urls.length - 1"
                @click.stop="moveImage(index, 'right')"
              >
                <IHeroiconsChevronRight class="size-4 text-zinc-600" />
              </button>
            </div>

            <!-- Delete button -->
            <button
              type="button"
              class="absolute top-1 right-1 bg-white/80 hover:bg-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
              @click.stop="removeImage(index)"
            >
              <IHeroiconsSolidXMark class="size-4 text-red-600" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <p v-else-if="!uploading" class="text-sm text-center text-zinc-500">
      Noch keine Bilder ausgewählt
    </p>
  </div>
</template>
